import { type GetStaticPaths, type GetStaticProps } from "next";
import Balancer from "react-wrap-balancer";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import React, { type FC, useEffect } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import {
  getIntegration,
  getSimpleIntegrations,
  type Integration,
  type Customer,
  type IntegrationSimple,
  getClient,
  groq,
  getAnnouncementBanner,
  type Banner,
  getDefaultLogos,
} from "lib/sanity";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import IntegrationsExplainerVideo from "components/integrations-explainer-video";
import { QueryTypes } from "components/integrations/connection/query-types";
import PaginatedLinks from "components/integrations/landing/paginated-links";
import { BeforeAfterHightouch } from "components/integrations/landing/before-after-hightouch";
import NotFound from "pages/404";
import { StackedLogos } from "components/logos";
import { HeroCTA } from "components/hero";
import { TextTitle } from "ui/text";
import { DirectionalButton } from "ui/button";
import { Breadcrumbs } from "ui/breadcrumbs";
import { integrationsCount } from "../../../constants/integrations";

type SourceProps = {
  source: Integration;
  defaultLogos: Customer[];
  destinations: IntegrationSimple[];
  slug: string;
  announcementBanner?: Banner;
};

const Source: FC<Readonly<SourceProps>> = ({ source, destinations, defaultLogos, slug, announcementBanner }) => {
  const router = useRouter();

  useEffect(() => {
    if (source) {
      trackEvent("Integration Page Viewed", {
        integration_name: source?.name,

        integration_status: source?.status,

        integration_type: "source",
      });
    }
  }, [source]);

  if (!router.isFallback && (!slug || !source)) {
    return <NotFound />;
  }

  if (!source) {
    return null;
  }

  const docsSlug = slug.includes("-source") ? slug.replace("-source", "") : slug;

  return (
    <Page
      title={`Sync data from ${source.name} with Hightouch.`}
      description={
        source.shortPitch ?? `Hightouch allows you to sync data from ${source.name} to ${integrationsCount}+ tools.`
      }
      announcementBanner={announcementBanner}
    >
      <Section my="0">
        <Breadcrumbs
          display="flex"
          justifyContent={{ base: "start", md: "center" }}
          mt={{ base: "small", md: "big", lg: "bigger" }}
          mb={{ base: "bigger", md: "bigger" }}
          crumbs={[
            { title: "Integrations", href: "/integrations" },
            { title: "Sources", href: "/integrations?type=source" },
          ]}
        />
        <TextTitle
          size="xl"
          align="center"
          titleAs="h1"
          title={
            <Flex alignItems="center" gap={{ base: "tiny" }}>
              <Box width={{ base: "60px", lg: "90px" }} height={{ base: "60px", lg: "90px" }} pos="relative">
                <Image
                  priority
                  src={source.iconUrl ? source.iconUrl + "?w=180" : ""}
                  alt={`${source.name}.`}
                  layout="fill"
                  objectFit="contain"
                />
              </Box>
              {source.name}
            </Flex>
          }
          descriptionAs="h2"
          description={source.shortPitch}
          actions={
            <HeroCTA
              hasDemoLink
              primaryAction="signup"
              signupText="Try now"
              demoLinkParameters={{ sourceName: source.name }}
            />
          }
        />
      </Section>

      <IntegrationsExplainerVideo />

      <StackedLogos customers={defaultLogos} my="0" pb={sectionSpacingY} />

      <Section theme="dark" bg="new.gray.950" themeBgColor="new.gray.950" color="brand.text.dark">
        <TextTitle
          theme="dark"
          size="lg"
          title={<Balancer>Get data out of {source.name}. Fast.</Balancer>}
          description={
            <>
              You&apos;ve spent time getting data into {source.name}, modeling it for analytics, and paying to store it.
              Why do that again with a traditional CDP? With Hightouch, you can activate your {source.name} data in less
              than 5 minutes. You no longer have to worry about storing your data with a 3rd party, complex CDP
              implementations, or having multiple data silos.
            </>
          }
          actions={
            <DirectionalButton href={`/docs/sources/${docsSlug}`} pl="0">
              Read our {source.name} documentation
            </DirectionalButton>
          }
        />
      </Section>

      {source.supportedQueryTypes && source.supportedQueryTypes.length > 1 && <QueryTypes source={source} />}

      <PaginatedLinks
        primaryIntegration={source}
        variant="allDestinations"
        allIntegrations={destinations.sort((a, b) => a.name.localeCompare(b.name))}
      />

      <BeforeAfterHightouch />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<SourceProps> = async ({ params }) => {
  const slug = params?.slug ? String(params?.slug) : "";
  const [destinations, source, announcementBanner, defaultLogos] = await Promise.all([
    getSimpleIntegrations("destination"),
    getIntegration(slug, "source"),
    getAnnouncementBanner(),
    getDefaultLogos(),
  ]);

  if (!source) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      slug,
      destinations,
      source,
      announcementBanner,
      defaultLogos,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(
    groq`*[_type == "integration" && type == "source" && status == "released"] { slug }`,
  );

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default Source;
